/**
 * Date and time formatting — doc 41 §6, implemented once.
 *
 * (H) One format per context, never locale-guessed: an ambiguous date on a
 * claim is a timely-filing error. (H) Never `MM/DD/YYYY` or `DD/MM/YYYY`.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export class DateFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DateFormatError";
  }
}

/** `YYYY-MM-DD`. A calendar fact: a date of service, a due date, a filing limit. */
export type BusinessDateString = string;
/** RFC 3339 instant, e.g. `2026-01-15T19:22:00Z`. */
export type InstantString = string;

const BUSINESS_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * doc 41 §6: `DD MMM YYYY`, or `DD MMM YY` compact for dense tables.
 *
 * (H) A business date is never timezone-converted, so this function never
 * constructs a `Date`. It splits the string. Constructing `new Date("2026-01-15")`
 * yields midnight UTC, which in `America/Los_Angeles` renders as 14 Jan — the
 * exact off-by-one-day defect that makes a filing deadline wrong.
 * (H) A calendar date never shows a timezone, because it does not have one.
 */
export function formatBusinessDate(
  value: BusinessDateString,
  options: { compact?: boolean } = {},
): string {
  const match = BUSINESS_DATE.exec(value.trim());
  if (!match) {
    throw new DateFormatError(
      `BusinessDate expects a calendar date as YYYY-MM-DD, got ${JSON.stringify(value)}. ` +
        "A timestamp is not a business date — use <DateTime> for an instant.",
    );
  }
  const [, year, month, day] = match;
  const monthIndex = Number.parseInt(month ?? "", 10) - 1;
  const name = MONTHS[monthIndex];
  if (name === undefined) {
    throw new DateFormatError(`Month out of range in ${JSON.stringify(value)}.`);
  }
  const yearText = options.compact === true ? (year ?? "").slice(2) : (year ?? "");
  return `${day} ${name} ${yearText}`;
}

interface TimestampParts {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
  zone: string;
}

function timestampParts(value: InstantString, timeZone: string): TimestampParts {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) {
    throw new DateFormatError(`Not a parseable instant: ${JSON.stringify(value)}.`);
  }
  let parts;
  try {
    // Locale is pinned to en-US for one reason only: it is the locale whose
    // ICU data yields the US timezone abbreviations doc 41 §6 shows (`EST`,
    // `CST`), where en-GB yields `GMT-5`. Nothing about the visible format is
    // taken from the locale — every part is picked out of `formatToParts` and
    // reassembled, so the output is `DD MMM YYYY HH:mm TZ` regardless.
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZoneName: "short",
    }).formatToParts(instant);
  } catch {
    throw new DateFormatError(`Unknown IANA time zone: ${JSON.stringify(timeZone)}.`);
  }
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    day: pick("day"),
    month: pick("month"),
    year: pick("year"),
    hour: pick("hour"),
    minute: pick("minute"),
    zone: pick("timeZoneName"),
  };
}

/**
 * doc 41 §6: `DD MMM YYYY HH:mm` plus the timezone abbreviation.
 * (H) The timezone is always shown (doc 03 R-XC-64).
 */
export function formatTimestamp(value: InstantString, timeZone: string): string {
  const p = timestampParts(value, timeZone);
  return `${p.day} ${p.month} ${p.year} ${p.hour}:${p.minute} ${p.zone}`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * doc 41 §6: relative form for recent activity only, with the absolute value
 * available in a tooltip. Anything older than a week gets the absolute date,
 * because "37d ago" is not information anybody can act on.
 */
export function formatRelative(value: InstantString, now: Date, timeZone: string): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) {
    throw new DateFormatError(`Not a parseable instant: ${JSON.stringify(value)}.`);
  }
  const deltaMs = now.getTime() - instant.getTime();
  if (deltaMs < 0) return formatTimestamp(value, timeZone);
  if (deltaMs < MINUTE_MS) return "just now";
  if (deltaMs < HOUR_MS) return `${Math.floor(deltaMs / MINUTE_MS)}m ago`;
  if (deltaMs < DAY_MS) return `${Math.floor(deltaMs / HOUR_MS)}h ago`;
  if (deltaMs < 7 * DAY_MS) return `${Math.floor(deltaMs / DAY_MS)}d ago`;
  return formatTimestamp(value, timeZone);
}

/**
 * doc 41 §6: `12d left` / `4h left`, always shown alongside the absolute due
 * date. Never the only signal (doc 40 §4).
 */
export function formatRemaining(remainingMs: number): string {
  if (remainingMs <= 0) return "Overdue";
  if (remainingMs >= DAY_MS) return `${Math.floor(remainingMs / DAY_MS)}d left`;
  if (remainingMs >= HOUR_MS) return `${Math.floor(remainingMs / HOUR_MS)}h left`;
  return `${Math.max(Math.floor(remainingMs / MINUTE_MS), 1)}m left`;
}
